import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MoveToStatusComponent } from '@components/move-to-status/move-to-status.component';
import { ISignalRNotification, NotificationType } from '@interfaces/signalr-notification';
import { AppDialogService } from '@services/app-dialog.service';
import { LogService } from '@services/log.service';
import { SignalRService } from '@services/signalr.service';
import { merge, Observable, of, Subject } from 'rxjs';
import { finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DSRESTClient } from '../../../shared/communication-objects/restclient.service';
import { MoveUsersConfirmationDialogComponent } from '../components/move-users-confirmation-dialog/move-users-confirmation-dialog.component';
import { MoveUsersDialogComponent } from '../components/move-users-dialog/move-users-dialog.component';
import { keysToCamelCase } from '../../../utilities/utils';

export interface MoveableUser {
  userId: number; // System User ID
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  organizationName: string;
  direction?: string; // Direction of the move (added during the move process)
}

export interface IMoveDirection {
  direction: string;
  userId: number;
}

export interface IUser {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  billableOrgName: string;
  modifiedByUserName?: string;
  direction?: string;
  destinationOrgName?: string;
  billableOrg?: number;
}

export interface IMoveStatusItem {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  originalBillableOrg: string;
  originalOrg: string;
  destinationBillableOrg: string;
  destinationOrg: string;
  modifiedOn: Date | string;
  modifiedByUserName: string;
  status: string;
  direction: string;
}

export interface IDirectionResponse {
  users: IMoveDirection[];
  destinationOrgName: string;
  billableOrgName: string;
  billableOrg: number;
}

export interface IMoveUserRequest {
  destinationOrg: number;
  userIds: number[];
}

export interface IMoveUserResult {
  userId: number;
  status: string;
  direction?: string;
}

export interface IMoveUserResponse {
  success: boolean;
  results: IMoveUserResult[];
}

@Injectable()
export class MoveUserService implements OnDestroy {
  // Constants
  private readonly apiURL = 'user/move-users';

  // Static properties and methods
  public static readonly MoveDirections = {
    Down: 'down',
    Up: 'up',
    Sideways: 'sideways',
    Same: 'same',
  };

  public static getIconForDirection(direction: string): string {
    return direction === MoveUserService.MoveDirections.Down
      ? 'arrow_downward'
      : direction === MoveUserService.MoveDirections.Up
      ? 'arrow_upward'
      : direction === MoveUserService.MoveDirections.Sideways
      ? 'swap_horiz'
      : 'info';
  }

  public static getColorForDirection(direction: string): string {
    return direction === MoveUserService.MoveDirections.Down
      ? 'red'
      : direction === MoveUserService.MoveDirections.Up
      ? 'green'
      : direction === MoveUserService.MoveDirections.Sideways
      ? 'red'
      : 'default_color';
  }

  public static isValidDirection(direction: string): boolean {
    return [
      MoveUserService.MoveDirections.Down,
      MoveUserService.MoveDirections.Up,
      MoveUserService.MoveDirections.Sideways,
    ].includes(direction);
  }

  // Instance properties
  private moveMessageSubject = new Subject<ISignalRNotification>();
  public moveMessages$ = this.moveMessageSubject.asObservable();

  private dialogClosedSubject = new Subject<boolean>();
  public dialogClosed$ = this.dialogClosedSubject.asObservable();

  private destroy$ = new Subject<void>();

  // Dialog references
  private moveStatusDialogRef: MatDialogRef<MoveToStatusComponent>;
  private lastStatusData: any[] = [];

  constructor(
    private _logger: LogService,
    private dialog: MatDialog,
    private _ads: AppDialogService,
    private restClient: DSRESTClient,
    private signalRService: SignalRService
  ) {
    // Initialize the SignalR connection
    this.signalRService.initializeConnection();

    // Subscribe to move-related messages
    this.signalRService
      .getMessagesBySource('move')
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        this.moveMessageSubject.next(notification);
        this.updateLastStatusData(notification);
      });
  }

  // Public methods for user movement
  moveUsers(
    selectedUsers: MoveableUser[] | IUser[],
    csoId: number,
    csoBillableOrgId: number,
    csoBillableOrgName: string
  ): Observable<boolean> {
    const moveUserDialog = this.showMoveUsersDialog(
      selectedUsers as IUser[],
      csoId,
      csoBillableOrgId,
      csoBillableOrgName
    );
    return this.handleMoveUsersDialogResult(moveUserDialog).pipe(
      switchMap(dialogResult => {
        if (!dialogResult) {
          return of(false);
        }
        return this.processMoveWithConfirmation(
          selectedUsers as IUser[],
          dialogResult.destinationOrg,
          csoBillableOrgId,
          dialogResult.selectedBillableOrgId ?? csoBillableOrgId
        );
      })
    );
  }

  /**
   * Handles the result from the move users dialog
   */
  private handleMoveUsersDialogResult(
    dialogRef: MatDialogRef<MoveUsersDialogComponent>
  ): Observable<any> {
    return merge(
      dialogRef.componentInstance.onContinue.asObservable(),
      dialogRef.componentInstance.onCancel.asObservable()
    ).pipe(
      finalize(() => dialogRef.close()),
      take(1)
    );
  }

  /**
   * Processes the move after getting destination org and confirmation
   */
  private processMoveWithConfirmation(
    users: IUser[],
    destinationOrg: number,
    csoBillableOrgId: number,
    selectedBillableOrgId: number
  ): Observable<boolean> {
    const showBillableOrgWarning =
      selectedBillableOrgId != null && selectedBillableOrgId !== csoBillableOrgId;
    return this.determineMoveDirection(users, destinationOrg).pipe(
      switchMap(destinations => {
        const preparedUsers = this.prepareUsersForConfirmation(users, destinations);
        return this.showAndHandleConfirmationDialog(
          preparedUsers,
          destinationOrg,
          showBillableOrgWarning
        );
      })
    );
  }

  /**
   * Prepares user data with direction and organization information
   */
  private prepareUsersForConfirmation(users: IUser[], destinations: IDirectionResponse): IUser[] {
    // Assign direction and organization info to each user
    users.forEach(user => {
      user['direction'] = destinations.users.find(dir => dir.userId === user.userId)?.direction;
      user['destinationOrgName'] = destinations.destinationOrgName;
      user['billableOrgName'] = destinations.billableOrgName;
      user['billableOrg'] = destinations.billableOrg;
    });

    // Sort users by direction, lastName, firstName, userName
    return users.sort(
      (a, b) =>
        a.direction.localeCompare(b.direction) ||
        a.lastName.localeCompare(b.lastName) ||
        a.firstName.localeCompare(b.firstName) ||
        a.userName.localeCompare(b.userName)
    );
  }

  /**
   * Shows confirmation dialog and handles the result
   */
  private showAndHandleConfirmationDialog(
    users: IUser[],
    destinationOrg: number,
    showBillableOrgWarning: boolean
  ): Observable<boolean> {
    return this.showUserMoveConfirmationDialog(users, showBillableOrgWarning)
      .afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }
          return this.initiateMoveProcess(users, destinationOrg);
        })
      );
  }

  /**
   * Initiates the actual move process after confirmation
   */
  private initiateMoveProcess(users: IUser[], destinationOrg: number): Observable<boolean> {
    const initialStatusData = this.prepareInitialStatusData(users);
    this._logger.log('Initial status data:', initialStatusData);

    // Show the status dialog immediately with initial "Waiting" status
    this.showMoveStatusDialog(initialStatusData);

    // Add a longer delay to ensure the dialog is fully rendered and initialized
    return this.waitForDialogInitialization().pipe(
      switchMap(() => {
        // Use the new streaming method instead of performUserMove
        return new Observable<boolean>(observer => {
          this.moveUsersStream({
            destinationOrg,
            userIds: users.map(u => u.userId)
          }).subscribe({
            next: (notification) => {
              // Forward each notification to the moveMessageSubject for UI updates
              this.moveMessageSubject.next(notification);
            },
            error: (err) => {
              this.handleMoveError(err);
              observer.error(err);
            },
            complete: () => {
              // You may want to check for overall success here
              this.handleMoveCompletion({ success: true });
              observer.next(true);
              observer.complete();
            }
          });
        });
      })
    );
  }

  /**
   * Creates initial status data for all users
   */
  private prepareInitialStatusData(users: IUser[]): IMoveStatusItem[] {
    return users.map(user => {
      return {
        userId: user.userId,
        userName: user.userName,
        firstName: user.firstName,
        lastName: user.lastName,
        originalBillableOrg: user.billableOrgName,
        originalOrg: user.organizationName,
        destinationBillableOrg: user.billableOrgName,
        destinationOrg: user.destinationOrgName || '',
        modifiedOn: new Date(),
        modifiedByUserName: user.modifiedByUserName || 'System',
        status: 'Waiting',
        direction: user.direction || 'Unknown',
      };
    });
  }

  /**
   * Returns an observable that resolves when dialog is ready
   */
  private waitForDialogInitialization(): Observable<void> {
    return new Observable<void>(observer => {
      this._logger.log('Waiting for dialog to initialize before starting move process...');
      // Small delay to ensure dialog is initialized
      this.moveStatusDialogRef
        .afterOpened()
        .pipe(take(1))
        .subscribe(() => {
          this._logger.log('Dialog fully initialized, starting move process now');
          observer.next();
          observer.complete();
        });
    });
  }

  /**
   * Performs the actual move operation after dialog initialization
   */
  private performMoveOperation(users: IUser[], destinationOrg: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      // Ensure SignalR connection is established
      this.ensureSignalRConnection().then(() => {
        // Clear any previous messages that might be in the queue
        this._logger.log('Clearing any previous messages before starting move process');

        // Add a manual notification to indicate the move is starting
        this.sendStartNotification();

        setTimeout(() => {
          this._logger.log('Starting move process now');
          this.performUserMove(users, destinationOrg).subscribe(
            (result: { success: boolean }) => {
              this.handleMoveCompletion(result);
              observer.next(result.success);
              observer.complete();
            },
            error => {
              this.handleMoveError(error);
              observer.error(error);
            }
          );
        }, 350);
      });
    });
  }

  /**
   * Sends a notification that the move process is starting
   */
  private sendStartNotification(): void {
    const startNotification: ISignalRNotification = {
      message: 'Move process starting',
      source: 'MoveUser',
      type: NotificationType.General,
      timestamp: new Date().toISOString(),
    };
    this.moveMessageSubject.next(startNotification);
  }

  /**
   * Handles the completion of the move process
   */
  private handleMoveCompletion(_result: { success: boolean }): void {
    if (_result.success) {
      this._ads.showSuccessMessage('Move process completed successfully.');
    } else {
      this._ads.showErrorMessage('Move process completed with failures.');
    }
  }

  /**
   * Handles errors during the move process
   */
  private handleMoveError(error: any): void {
    this._logger.error('Error starting move process:', error);

    // Add a manual notification to indicate the error
    const errorNotification: ISignalRNotification = {
      message: 'Error starting move process',
      source: 'MoveUser',
      type: NotificationType.Error,
      timestamp: new Date().toISOString(),
      data: { error: error.message || 'Unknown error' },
    };
    this.moveMessageSubject.next(errorNotification);
  }

  determineMoveDirection(
    _selectedUsers: { userId: number }[],
    destinationOrg: number
  ): Observable<IDirectionResponse> {
    const apiResponse = this.restClient.postForResponse<IDirectionResponse>(
      `${this.apiURL}/direction/${destinationOrg}`,
      {
        userIds: _selectedUsers.map(user => user.userId),
      }
    );

    return apiResponse.pipe(
      finalize(() => {
        this._logger.log('Direction determination complete for users:', _selectedUsers);
      })
    );
  }

  performUserMove(
    selectedUsers: IUser[],
    _destinationOrgId: number
  ): Observable<{ success: boolean; data: IMoveStatusItem[] }> {
    const request: IMoveUserRequest = {
      destinationOrg: _destinationOrgId,
      userIds: selectedUsers.map(user => user.userId),
    };

    // Create a map of users by userId for quick lookup when receiving notifications
    const userMap = new Map<number, IUser>();
    selectedUsers.forEach(user => {
      userMap.set(user.userId, user);
    });

    // Send the move request to the server
    return this.restClient.postForResponse<IMoveUserResponse>(`${this.apiURL}`, request).pipe(
      map(response => {
        return {
          success: response.success,
          data: selectedUsers.map(user => {
            const result = response.results.find(r => r.userId === user.userId);
            return {
              userId: user.userId,
              userName: user.userName,
              firstName: user.firstName,
              lastName: user.lastName,
              originalBillableOrg: user.billableOrgName,
              originalOrg: user.organizationName,
              destinationBillableOrg: user.billableOrgName,
              destinationOrg: user.destinationOrgName || '',
              modifiedOn: new Date(),
              modifiedByUserName: user.modifiedByUserName || 'System',
              // Use the result status if available, otherwise keep as "Waiting"
              status: result?.status || 'Waiting',
              direction: result?.direction || user.direction || 'Unknown',
            };
          }),
        };
      }),
      tap(result => {
        if (result.success) {
          // Show success message when the operation completes
          this._ads.showSuccessMessage('User move operation initiated successfully');
        }
      })
    );
  }

  // SignalR related methods
  private ensureSignalRConnection(): Promise<void> {
    return new Promise<void>(resolve => {
      this.signalRService.connectionEstablished$
        .pipe(
          take(1),
          tap(established => {
            if (established) {
              this._logger.log('SignalR connection is already established');
              resolve();
            } else {
              this._logger.log('Waiting for SignalR connection to be established...');
              this.signalRService.connectionEstablished$
                .pipe(
                  takeUntil(this.destroy$),
                  tap(isEstablished => {
                    if (isEstablished) {
                      this._logger.log('SignalR connection established');
                      resolve();
                    }
                  })
                )
                .subscribe();
            }
          })
        )
        .subscribe();
    });
  }

  // Dialog related methods
  private showMoveUsersDialog(
    selectedUsers: IUser[],
    csoId: number,
    csoBillableOrgId: number,
    csoBillableOrgName: string
  ): MatDialogRef<MoveUsersDialogComponent> {
    const dialogRef = this.dialog.open(MoveUsersDialogComponent, {
      width: '80%',
      disableClose: true, // Prevent closing by clicking outside or pressing ESC
      data: {
        selectedUsers,
        csoId,
        csoBillableOrgId,
        csoBillableOrgName,
      },
    });
    return dialogRef;
  }
  private showUserMoveConfirmationDialog(
    selectedUsers: IUser[],
    showBillableOrgWarning: boolean
  ): MatDialogRef<MoveUsersConfirmationDialogComponent> {
    return this._ads.showDialogReturnRef(MoveUsersConfirmationDialogComponent, {
      selectedUsers,
      showBillableOrgWarning,
    });
  }

  private showMoveStatusDialog(selectedUsers: IMoveStatusItem[]) {
    this._logger.log('showMoveStatusDialog called with data:', selectedUsers);

    // If there's already a dialog open, close it first
    if (this.moveStatusDialogRef) {
      this._logger.log('Closing existing move status dialog');
      this.moveStatusDialogRef.close();
      // Small delay to ensure previous dialog is fully closed
      this.moveStatusDialogRef = null;
    }
    this.openMoveStatusDialog(selectedUsers);
  }

  private openMoveStatusDialog(selectedUsers: IMoveStatusItem[]) {
    // Ensure all users have a status of 'Waiting' if not already set
    const usersWithStatus = selectedUsers.map(user => {
      // Check if the status is final (completed or failed)
      const finalStatuses = [
        'success',
        'complete',
        'done',
        'error',
        'failed',
        'rejected',
        'skipped',
      ];
      const isFinalStatus =
        user.status && finalStatuses.some(status => user.status.toLowerCase().includes(status));

      //fix ModifiedOn format
      user['modifiedOn'] = new Date(user.modifiedOn).toLocaleString();
      // If the status is not final, set it to 'Waiting'
      if (!isFinalStatus) {
        // Log if we're changing the status
        if (user.status && user.status !== 'Waiting') {
          this._logger.log(`Changing user ${user.userId} status from ${user.status} to Waiting`);
        }
        return { ...user, status: 'Waiting' };
      }
      return user;
    });

    // Store the last status data
    this.lastStatusData = [...usersWithStatus];

    const columns = [
      'userName',
      'firstName',
      'lastName',
      'userId',
      'originalBillableOrg',
      'originalOrg',
      'destinationBillableOrg',
      'destinationOrg',
      'modifiedOn',
      'modifiedByUserName',
      'status',
      'direction',
    ];

    this._logger.log('Opening move status dialog');

    // Store dialog reference as a class property so we can access it later
    this.moveStatusDialogRef = this.dialog.open(MoveToStatusComponent, {
      panelClass: 'cso-dialog',
      width: '85%',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true, // Prevent closing by clicking outside or pressing ESC
      data: {
        results: usersWithStatus,
        showToListButton: false,
        displayedColumns: columns,
        exportToExcelFileName: 'User_Move_Status',
        title: 'MoveForms.UserMove.MoveStatus',
        subtitle: 'MoveForms.UserMove.MoveStatusSubtitle',
        moveMessages$: this.moveMessages$,
      },
    });

    const moveToStatusComponent: MoveToStatusComponent = this.moveStatusDialogRef.componentInstance;

    moveToStatusComponent.close.pipe(take(1)).subscribe(() => {
      this._logger.log('Close event received, isProcessing:', moveToStatusComponent.isProcessing);

      // Only allow closing if all items are complete or processing is done
      if (!moveToStatusComponent.isProcessing || moveToStatusComponent.areAllItemsComplete()) {
        this._logger.log('Closing all dialogs');
        this.dialog.closeAll();

        // Emit that the dialog was closed after a completed move
        const allComplete = moveToStatusComponent.areAllItemsComplete();
        this.dialogClosedSubject.next(allComplete);

        // Clear the last status data if all items are complete
        if (allComplete) {
          this.lastStatusData = [];
        }
      }
    });

    /* COLUMNS defined in the JIRA ticket SBTMTAPWP-3041
        User Name
        First Name
        Last Name
        System User ID
        Original Billable Org
            Billable Organization from which the User was moved
        Original Org
            Organization from which the User was moved
        Destination Billable Org
            Billable Organization to which the User was moved to
        Destination Org
            Organization to which the User was moved to
        Date Stamp
        User Name
        Status
    */
  }

  // Status update methods
  private updateLastStatusData(notification: ISignalRNotification) {
    if (!notification || !this.lastStatusData || this.lastStatusData.length === 0) {
      return;
    }

    // Log all notifications for debugging
    this._logger.log('Updating status data with notification:', notification);

    // Check if this is a general notification about the move process
    if (
      notification.type === NotificationType.General &&
      notification.message &&
      !notification.data
    ) {
      this._logger.log('Received general move notification:', notification.message);

      // Check if this is a start message
      const message = notification.message.toLowerCase();
      if (message.includes('start') || message.includes('begin') || message.includes('initiat')) {
        // Update all items to "In Progress" status
        this.lastStatusData.forEach(item => {
          if (item.status === 'Waiting') {
            item.status = 'In Progress';
          }
        });
        this._logger.log('Updated all waiting items to In Progress');
      }

      return;
    }

    // If we have item data, update the specific item
    if (notification.data) {
      const itemData = notification.data;
      const itemId = this.getItemId(itemData);

      if (itemId !== undefined) {
        // Find the item in lastStatusData
        const itemIndex = this.lastStatusData.findIndex(item => this.getItemId(item) === itemId);

        if (itemIndex >= 0) {
          // Update the item's status
          const previousStatus = this.lastStatusData[itemIndex].status;
          this.lastStatusData[itemIndex].status = itemData.status || notification.message;
          this._logger.log(
            `Updated status for item ${itemId} from ${previousStatus} to: ${this.lastStatusData[itemIndex].status}`
          );
        } else {
          this._logger.log(`Item ${itemId} not found in lastStatusData`);
        }
      } else {
        this._logger.log('Could not determine item ID from notification data:', itemData);
      }
    }

    // Check if all items are processed
    const allProcessed = this.lastStatusData.every(
      item =>
        item.status !== 'Waiting' && item.status !== 'In Progress' && item.status !== 'Processing'
    );

    if (allProcessed) {
      this._logger.log('All items have been processed');
    }
  }

  private getItemId(item: {
    userId?: number;
    id?: number;
    tankId?: number;
    locationId?: number;
  }): number | undefined {
    if (!item) return undefined;

    // Try common ID properties
    if (item.userId !== undefined) return item.userId;
    if (item.id !== undefined) return item.id;
    if (item.tankId !== undefined) return item.tankId;
    if (item.locationId !== undefined) return item.locationId;

    return undefined;
  }

  // Lifecycle methods
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.moveStatusDialogRef) {
      this.moveStatusDialogRef.close();
    }
  }

  // Add this method to support SSE streaming for move users
  moveUsersStream(request: IMoveUserRequest): Observable<ISignalRNotification> {
    return new Observable<ISignalRNotification>(observer => {
      // First make a POST request to initiate the operation
      this.restClient.postForResponse<{ streamId: string }>(`${this.apiURL}/initiate`, request)
        .subscribe({
          next: (response) => {
            // Create EventSource connection using the returned stream URL
            const eventSource = new EventSource(`api/v1/${this.apiURL}/stream/${response.streamId}`);

            // Handle incoming messages
            eventSource.onmessage = (event) => {
              try {
                const notification = keysToCamelCase(JSON.parse(event.data));
                this._logger.log('Received notification from SSE:', notification);

                // Check if this is a completion message
                if (notification.type === NotificationType.Success &&
                    notification.message?.toLowerCase().includes('completed')) {
                  observer.next(notification);
                  observer.complete();
                  eventSource.close();
                } else {
                  observer.next(notification);
                }
              } catch (error) {
                this._logger.error('Error parsing SSE notification:', error);
              }
            };

            // Handle errors
            eventSource.onerror = (error) => {
              this._logger.error('SSE connection error:', error);
              eventSource.close();
              observer.error(error);
            };

            // Cleanup when the observable is unsubscribed
            return () => {
              eventSource.close();
            };
          },
          error: (error) => {
            this._logger.error('Error initiating move operation:', error);
            observer.error(error);
          }
        });
    });
  }
}
